import type { Adjustment, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../modelPaths"
import { StorageChainConfig, MassStorageOptions } from "../../RackTypes"
import { getMsHardwareCount, MsUnitShelfData, MsShelfData } from "./ms-hardware-counting-utils"
import { assignShelfPlacement } from "./ms-shelf-utils"
import { getMsBoltPackCount } from "./ms-bolt-utils"
import { BuyRequest } from "@/js/services/RogueTypes"

function removeSide(basePart: PartDefinition, direction: 'left' | 'right') {
  if (direction === 'left') {
    basePart.slots.front = []
    basePart.slots.back = []
  }

  // Clear the slots on the side we are attaching to
  Object.keys(basePart.slots).forEach((key) => {
    if (
      key.startsWith('crossmember_top_')
      || key.startsWith(`${direction}_side_`)
      || key === `${direction}_side_corner`
    ) {
      basePart.slots[key] = []
    }
  })
}

// Addon Build
export function addMassStorageUnitSide(
  basePart: PartDefinition,
  addonChain: StorageChainConfig[],
  direction: 'left' | 'right',
  config: MassStorageOptions,
  frontWheel: PartDefinition,
  backWheel: PartDefinition,
  buyRequest: BuyRequest
) {
  if (!addonChain.length) return

  // Clear the specified side so uprights can be reattached cleanly
  removeSide(basePart, direction)

  // const is100BaseNeeded = addonChain[0].heightSelection === '100'
  const initialBaseHeight = config.height

  // Count the base unit at the SAME positions it renders at. assignShelfPlacement is the single
  // source of truth for MS shelf placement; getBaseShelfDataForHardware (shared with MMS) uses
  // different spacing/ranges and dumbbell rules, which would desync the base's hardwareSlotNames
  // from the addon units and break shared-bolt detection at the base↔addon seam (over-counts kits).
  // A throwaway target captures the returned shelf data without mutating the already-rendered base.
  const baseShelves = assignShelfPlacement({ mesh: '', slots: {} }, {
    shelfOneSelection   : config.shelfOneSelection,
    shelfTwoSelection   : config.shelfTwoSelection,
    shelfThreeSelection : config.shelfThreeSelection,
    shelfFourSelection  : config.shelfFourSelection,
    shelfFiveSelection  : config.shelfFiveSelection,
    heightSelection     : config.height,
    widthSelection      : config.width,
  } as StorageChainConfig, config)

  let currentBase = basePart
  let previousHeight = initialBaseHeight // Track the previous height to handle transitions

  // An array to track shelf names for hardware counting
  const allUnitsShelves: MsUnitShelfData[] = [{
    unit       : 0,
    unitHeight : config.height,
    shelves    : baseShelves,
  }]
  let unitCounter = 1

  addonChain.forEach((addon) => {
    const addonHeight = addon.heightSelection

    // When adding to the left side, the new unit's right side connects to the previous unit
    // When adding to the right side, the new unit's left side connects to the previous unit
    // The shared side should always use the taller height
    const sharedHeight = Math.max(Number(previousHeight), Number(addonHeight)).toString()

    // Remove inner uprights from the previous unit if shared upright is taller
    if (Number(addonHeight) > Number(previousHeight)) {
      if (Array.isArray(currentBase.slots.front)) {
        currentBase.slots.front = []
      }
      if (Array.isArray(currentBase.slots.back)) {
        currentBase.slots.back = []
      }
    }

    // Build the addon unit's uprights - these go into the nextBase
    const nextFrontUpright: PartDefinition = { mesh: getRackModel('PARTS', `upright ${addonHeight}`), slots: {} }
    const nextBackUpright: PartDefinition = { mesh: getRackModel('PARTS', `upright ${addonHeight}`), slots: {}, adjustments: [{ type: 'rotate', axis: 'Y', amount: 180 }] }

    // Build the uprights for the shared connection - these are pushed to the parent's slots
    const sharedFrontUpright: PartDefinition = { mesh: getRackModel('PARTS', `upright ${sharedHeight}`), slots: {} }
    const sharedBackUpright: PartDefinition = { mesh: getRackModel('PARTS', `upright ${sharedHeight}`), slots: {}, adjustments: [{ type: 'rotate', axis: 'Y', amount: 180 }] }

    // Use shared height for crossmember as well
    const rightSideAdjustments: Adjustment[] = direction === 'right' ? [{ type: 'rotate' as const, axis: 'Y' as const, amount: 180 }] : []
    const sharedSideCrossmember: PartDefinition = { mesh: getRackModel('PARTS', 'crossmember'), slots: {}, anchor: direction === 'right' ? 'right' : '', adjustments: rightSideAdjustments }
    const endSideCrossmember: PartDefinition = { mesh: getRackModel('PARTS', 'crossmember'), slots: {}, anchor: direction === 'right' ? 'right' : '', adjustments: rightSideAdjustments }

    const corner: PartDefinition = { 
      mesh        : getRackModel('PARTS', 'corner'), 
      slots       : {}, 
      adjustments : direction === 'left' ? [{ type: 'rotate', axis: 'Y', amount: 90 }] : [], 
    }

    // Always clear previous add-on's side before attaching new one
    removeSide(currentBase, direction)


    // Build the other-side base
    const nextBase: PartDefinition = {
      mesh  : getRackModel('PARTS', 'base'),
      slots : {
        front                               : [nextFrontUpright],
        back                                : [nextBackUpright],
        [`crossmember_top_${addonHeight}h`] : [endSideCrossmember],
        front_wheel                         : addon.footSelection === 'wheels' ? frontWheel : [],
        back_wheel                          : addon.footSelection === 'wheels' ? backWheel : [],
      },
      anchor: 'base',
    }

    // Push the shared uprights onto the parent's arrays
    // These uprights must use the shared height to properly connect units of different heights
    if (!Array.isArray(currentBase.slots.front)) currentBase.slots.front = []
    const frontCurrentUprightSlot = currentBase.slots.front as PartDefinition[]
    frontCurrentUprightSlot.push(sharedFrontUpright)

    if (!Array.isArray(currentBase.slots.back)) currentBase.slots.back = []
    const backCurrentUprightSlot = currentBase.slots.back as PartDefinition[]
    backCurrentUprightSlot.push(sharedBackUpright)

    // Use the shared height for the crossmember slot
    currentBase.slots[`crossmember_top_${sharedHeight}h`] ??= []
    const currentCrossmemberSlot = currentBase.slots[`crossmember_top_${sharedHeight}h`] as PartDefinition[]
    currentCrossmemberSlot.push(sharedSideCrossmember)

    // Addon Corners
    if (addon.cornerQuantity) {
      const shelfKeys = ['shelfOneSelection', 'shelfTwoSelection', 'shelfThreeSelection', 'shelfFourSelection'] as const
      const slotNames = ['corner_one', 'corner_two', 'corner_three', 'corner_four']

      const cornerShelves: MsShelfData[] = []

      for (let i = 0; i < Number(addon.cornerQuantity); i += 1) {
        const shelfKey = shelfKeys[i]
        const slotName = slotNames[i]
        const isCornerSelected = addon[shelfKey] === 'corner'

        // For counting hardware
        if (isCornerSelected) {
          cornerShelves.push({ hardwareSlotName: 'corner', shelfType: 'corner' })
        }

        const isHeightValid = (slotName === 'corner_three' && ['70', '100'].includes(addon.heightSelection))
            || (slotName === 'corner_four' && addon.heightSelection === '100')
            || (slotName === 'corner_one' || slotName === 'corner_two')

        if (isHeightValid) {
          currentBase.slots[slotName] = isCornerSelected ? corner : []
        } else {
          currentBase.slots[slotName] = []
        }
      }

      // For counting hardware
      if (cornerShelves.length > 0) {
        allUnitsShelves.push({
          unit       : unitCounter,
          unitHeight : addon.heightSelection,
          shelves    : cornerShelves,
        })
        unitCounter += 1
      }
      
      if (direction === 'right') {
        corner.anchor = 'left'
        nextBase.adjustments = [{ axis: 'Y', type: 'rotate', amount: 180 }]
      }

    } else {
      // Addon Shelves
      const shelfTarget = direction === 'right' ? currentBase : nextBase

      // @TODO this line causes the error switch back from corner to  a normal rack width
      const shelvesForThisUnit = assignShelfPlacement(shelfTarget, addon, config)

      allUnitsShelves.push({ unit: unitCounter, unitHeight: addon.heightSelection, shelves: shelvesForThisUnit })
      unitCounter += 1
    }

    // Attach the child on the correct side
    if (addon.cornerQuantity) {
      currentBase.slots[`${direction}_side_corner`] = nextBase
    } else {
      currentBase.slots[`${direction}_side_${addon.widthSelection}h`] = nextBase
    }

    // Shift base and track previous height for the next addon chain
    currentBase = nextBase
    previousHeight = addon.heightSelection
  })

  // Add additional logic for hardware counting
  for (let i = 0; i < allUnitsShelves.length; i += 1) {
    const unit = allUnitsShelves[i]

    const isCorner = unit.shelves.every(s => s.hardwareSlotName === 'corner')

    // Mark first and last non-corner units as end units
    if (!isCorner) {
      const prev = allUnitsShelves[i - 1]
      const next = allUnitsShelves[i + 1]

      const isFirstNonCorner = !prev || prev.shelves.every(s => s.hardwareSlotName === 'corner')
      const isLastNonCorner = !next || next.shelves.every(s => s.hardwareSlotName === 'corner')

      unit.isEndUnit = isFirstNonCorner || isLastNonCorner

      // If either neighbor is a corner, mark as adjacent
      unit.adjacentToCorner = !!(prev && prev.shelves.some(s => s.hardwareSlotName === 'corner'))
        || !!(next && next.shelves.some(s => s.hardwareSlotName === 'corner'))
    }
  }

  const result = getMsHardwareCount(allUnitsShelves)

  // Add the hardwareCount to the BR as 4-pack / 16-pack quantities (MS has no shared/solo split)
  if (buyRequest.children?.[0]?.metadata) {
    buyRequest.children[0].metadata.hardwareCount = getMsBoltPackCount(result)
  }
}
